/**
 * The working pattern (piece 34, *The working pattern*; decision 12850; T3854):
 * the days of the week one employment works and the hours of each, dated from
 * the day it takes effect. 🔨 A row is never edited; a change is a new row from
 * its own day. 🔒 The hours are personal data read in the Employee page's Job
 * section; piece 29's Who Can Go and the Schedule read only whether a window
 * falls outside the pattern, and warn without refusing.
 */

import { t } from '../strings';
import { END_OF_DAY, clockLabel, weekdayLabel } from '../settings/timeAttendance';
import { calendarDayInZone, hasDayShape, wallClockInZone } from '../zonedTime';

/** One day of the pattern: its start and end wall clocks, `HH:MM` in the Organization's zone, or null for a day not worked. */
export type WorkingPatternDay = { startsAt: string; endsAt: string } | null;

/** 🔒 The seven days, Sunday first by the JavaScript day number, as the Work Calendar counts them. */
export type WorkingPatternDays = readonly WorkingPatternDay[];

/** The pattern a window asks to set, as Edit Working Pattern sends it. */
export interface WorkingPatternDraft {
  /** The first day the pattern is in force, `YYYY-MM-DD`. */
  effectiveFrom: string;
  /** Seven entries, Sunday first. */
  days: WorkingPatternDay[];
}

/** One stored pattern, as the Job section and the board read it. */
export interface WorkingPatternRow {
  id: string;
  effectiveFrom: string;
  days: WorkingPatternDays;
}

/** 🔒 What the Job section carries beside its rows for Edit Working Pattern: sent only to a reader who may set the pattern. */
export interface WorkingPatternSection {
  /** HR holding `hr.hire` or a manager above the job, on a job that has not ended (decision 12850). */
  mayEdit: boolean;
  /** The window's starting values: the pattern in force, else a weekday pattern of seven to half past three. */
  start: WorkingPatternDraft;
}

/** The order the line and the window list the days in: Monday first, Sunday last. */
export const WORKING_PATTERN_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/** A pattern nobody has set yet starts the window on the five weekdays, 7:00 AM to 3:30 PM. */
export const DEFAULT_WORKING_DAY: { startsAt: string; endsAt: string } = { startsAt: '07:00', endsAt: '15:30' };

/** Setting one job's working pattern, relative to the API root. */
export function workingPatternPath(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}/working-pattern`;
}

/** 🔒 The refusal of a save to anybody but HR with the hire permission or a manager above the job, in a sentence. */
export const WORKING_PATTERN_REFUSAL = 'A working pattern is set by HR or by a manager above this person, and this identity is neither.';

/** 🔒 An ended job takes no new pattern. */
export const WORKING_PATTERN_ENDED_REFUSAL = 'This job has ended, so no new working pattern is set on it.';

/** The window's starting draft: the pattern in force where there is one, else the five weekdays. */
export function workingPatternStart(inForce: WorkingPatternRow | null, today: string): WorkingPatternDraft {
  const days: WorkingPatternDay[] = inForce
    ? [...inForce.days]
    : [null, { ...DEFAULT_WORKING_DAY }, { ...DEFAULT_WORKING_DAY }, { ...DEFAULT_WORKING_DAY }, { ...DEFAULT_WORKING_DAY }, { ...DEFAULT_WORKING_DAY }, null];
  return { effectiveFrom: today, days };
}

const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * 🔨 Why a pattern is refused, as sentences, or an empty list: seven days, at
 * least one worked, each written `HH:MM` and ending after it starts, and a
 * first day written `YYYY-MM-DD` inside the job's own dates.
 */
export function workingPatternProblems(draft: WorkingPatternDraft, job?: { startDate: string; endDate: string | null }): string[] {
  const problems: string[] = [];
  if (!Array.isArray(draft?.days) || draft.days.length !== 7) return ['A working pattern names each of the seven days of the week.'];
  if (draft.days.every((day) => day === null)) problems.push('A working pattern names at least one day worked.');
  for (const [index, day] of draft.days.entries()) {
    if (day === null) continue;
    if (typeof day !== 'object' || !CLOCK.test(String(day.startsAt)) || !(CLOCK.test(String(day.endsAt)) || day.endsAt === END_OF_DAY)) {
      problems.push(`${weekdayLabel(index)}'s hours are written as a time of day.`);
    } else if (day.startsAt >= day.endsAt) {
      problems.push(`${weekdayLabel(index)} ends after it starts.`);
    }
  }
  if (typeof draft.effectiveFrom !== 'string' || !hasDayShape(draft.effectiveFrom)) problems.push('The pattern takes effect on a day written YYYY-MM-DD.');
  else if (job && draft.effectiveFrom < job.startDate) problems.push('The pattern takes effect on or after the day the job started.');
  else if (job?.endDate && draft.effectiveFrom > job.endDate) problems.push('The pattern takes effect on or before the job’s last day.');
  return problems;
}

/** The days a stored `days` value holds, read defensively: anything that is not seven days reads as no pattern at all. */
export function workingPatternDays(value: unknown): WorkingPatternDays | null {
  if (!Array.isArray(value) || value.length !== 7) return null;
  return value.map((day): WorkingPatternDay => {
    if (day && typeof day === 'object' && typeof (day as { startsAt?: unknown }).startsAt === 'string' && typeof (day as { endsAt?: unknown }).endsAt === 'string') {
      return { startsAt: (day as { startsAt: string }).startsAt, endsAt: (day as { endsAt: string }).endsAt };
    }
    return null;
  });
}

/**
 * 🔒 The pattern in force on a day: the row with the latest `effectiveFrom` on
 * or before it. The rows arrive newest written first among those sharing a day,
 * so the first of the latest day wins the tie. None reads as available.
 */
export function workingPatternOn<T extends { effectiveFrom: string }>(rows: readonly T[], day: string): T | null {
  let found: T | null = null;
  for (const row of rows) {
    if (row.effectiveFrom > day) continue;
    if (found === null || row.effectiveFrom > found.effectiveFrom) found = row;
  }
  return found;
}

/**
 * 🔒 Whether a window falls outside a pattern (decision 12850): the window's
 * day, read in the Organization's zone, is not worked, or the window starts
 * before that day's start or ends after its end. A window running into the next
 * day is outside unless the day works to midnight.
 */
export function outsideWorkingPattern(days: WorkingPatternDays, windowStart: Date | string, windowEnd: Date | string, zone: string): boolean {
  const day = calendarDayInZone(windowStart, zone);
  const weekday = new Date(`${day}T00:00:00.000Z`).getUTCDay();
  const hours = days[weekday] ?? null;
  if (hours === null) return true;
  const startWall = wallClockInZone(windowStart, zone);
  const endWall = wallClockInZone(windowEnd, zone);
  const startClock = startWall.slice(11, 16);
  const endDay = endWall.slice(0, 10);
  const endClock = endDay > day ? (endWall.slice(11, 16) === '00:00' && endDay === nextDay(day) ? END_OF_DAY : '99:99') : endWall.slice(11, 16);
  return startClock < hours.startsAt || endClock > hours.endsAt;
}

function nextDay(day: string): string {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, date + 1)).toISOString().slice(0, 10);
}

/**
 * 🔒 The pattern as one line, Monday first (decision 12850): days sharing hours
 * share a part, *Monday, Tuesday, Wednesday · 7:00 AM – 3:30 PM*, and days with
 * other hours follow after a point.
 */
export function workingPatternText(days: WorkingPatternDays): string {
  const groups: { names: string[]; hours: string }[] = [];
  for (const weekday of WORKING_PATTERN_DAY_ORDER) {
    const day = days[weekday] ?? null;
    if (day === null) continue;
    const hours = `${clockLabel(day.startsAt)} – ${clockLabel(day.endsAt)}`;
    const group = groups.find((row) => row.hours === hours);
    if (group) group.names.push(weekdayLabel(weekday));
    else groups.push({ names: [weekdayLabel(weekday)], hours });
  }
  if (groups.length === 0) return t('hr.pattern.none');
  return groups.map((group) => `${group.names.join(', ')} · ${group.hours}`).join(' · ');
}
